import { useTranslations } from "use-intl";

import { Chip } from "@/components/Chip/Chip";
import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { Image } from "@/components/Image/Image";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";
import type { TripDetails } from "@/features/trips/types/types";

type HotelCardProps = {
  hotel: TripDetails["hotels"][number];
};

function HotelCard({ hotel }: HotelCardProps) {
  return (
    <div className="w-60 overflow-clip rounded-2xl border border-stroke-neutral-soft">
      <Image
        src={hotel.imageUrl}
        alt={hotel.name}
        className="h-35 w-full object-cover"
      />

      <div className="px-4 py-3">
        <div className="mb-1 font-medium">{hotel.name}</div>
        <HotelStars stars={hotel.stars} />

        <ul className="mt-2 flex flex-wrap gap-2">
          {hotel.amenities.split(",").map((facility) => (
            <li key={facility} className="max-w-full">
              <Chip
                label={facility}
                color="neutral-soft"
                size="medium"
                maxWidth="full"
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type Props = {
  trip: TripDetails;
};

export function HotelsCard({ trip }: Props) {
  const t = useTranslations();

  return (
    <CollapsibleCard
      title={t("trips.hotels.title")}
      subtitle={t("trips.hotels.subtitle")}
      collapsible={false}
    >
      <ul className="-mx-4 flex snap-x snap-mandatory flex-nowrap gap-3 overflow-auto px-4">
        {trip.hotels.map((hotel) => (
          <li key={hotel.id} className="snap-center">
            <HotelCard hotel={hotel} />
          </li>
        ))}
      </ul>
    </CollapsibleCard>
  );
}
